import React, { useState, useEffect, useRef } from "react";
import ImageUploading from "react-images-uploading";
import { Button, Box, Divider, IconButton } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// Function to map language codes to numeric IDs
const mapLanguageToId = (language) => {
  const languageMap = { en: 1, fr: 2, ar: 3 };
  return languageMap[language] || null;
};

const isPDF = (file) => {
  return (
    file?.file?.type === "application/pdf" ||
    file?.data_url?.startsWith("data:application/pdf") ||
    file?.type === "pdf" ||
    (typeof file?.data_url === "string" &&
      file.data_url.toLowerCase().endsWith(".pdf"))
  );
};

export default function Attachments({
  showMainImage = true,
  showReport = true,
  onAttachmentsChange,
  media,
  currentLanguage,
  onDeleteMainImage,
  onDeleteAttachment,
}) {
  const [mainImage, setMainImage] = useState([]);
  const [deleteMainImage, setDeleteMainImage] = useState(false);
  const [attachmentsByLanguage, setAttachmentsByLanguage] = useState({});
  const [deletedAttachmentsByLanguage, setDeletedAttachmentsByLanguage] =
    useState({});

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (media) {
      if (media.mainImage) {
        setMainImage([{ data_url: media.mainImage }]);
      }
      const selectedLanguageId = mapLanguageToId(currentLanguage);
      if (media.attachments?.length > 0) {
        const filteredAttachments = media.attachments.filter(
          (attachment) => Number(attachment.languageId) === selectedLanguageId
        );
        const mappedAttachments = filteredAttachments.map((attachment) => ({
          id: attachment.id,
          data_url: attachment.url,
        }));
        setAttachmentsByLanguage((prev) => ({
          ...prev,
          [currentLanguage]: mappedAttachments,
        }));
      }
    }
  }, [media, currentLanguage]);

  const handleMainImageUpload = (imageList) => {
    setMainImage(imageList);
    setDeleteMainImage(false);

    if (onAttachmentsChange) {
      onAttachmentsChange({
        mainImage: imageList.length > 0 ? imageList : null,
        report: attachmentsByLanguage[currentLanguage] || [],
      });
    }
  };

  // Custom file handler for attachments (images + PDFs)
  const handleAttachmentFileChange = (event) => {
    const files = Array.from(event.target.files);
    const currentAttachments = attachmentsByLanguage[currentLanguage] || [];

    const processFiles = async () => {
      const newFiles = await Promise.all(
        files.map((file) => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                file: file,
                data_url: e.target.result,
                type: file.type.includes("pdf") ? "pdf" : "image",
                name: file.name,
              });
            };
            reader.readAsDataURL(file);
          });
        })
      );

      const updatedAttachments = [...currentAttachments, ...newFiles];

      setAttachmentsByLanguage((prev) => ({
        ...prev,
        [currentLanguage]: updatedAttachments,
      }));

      if (onAttachmentsChange) {
        onAttachmentsChange({
          mainImage: mainImage.length > 0 ? mainImage : null,
          report: updatedAttachments,
        });
      }
    };

    processFiles();

    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle Main Image Removal
  const handleRemoveMainImage = () => {
    setDeleteMainImage(true);
    setMainImage([]);

    if (onDeleteMainImage) {
      onDeleteMainImage(true);
    }

    if (onAttachmentsChange) {
      onAttachmentsChange({
        mainImage: null,
        report: attachmentsByLanguage[currentLanguage] || [],
      });
    }
  };

  // Handle Attachment Removal
  const handleRemoveAttachment = (index) => {
    const deletedId = attachmentsByLanguage[currentLanguage]?.[index]?.id;
    setDeletedAttachmentsByLanguage((prev) => ({
      ...prev,
      [currentLanguage]: [...(prev[currentLanguage] || []), deletedId],
    }));

    const updatedAttachments = attachmentsByLanguage[currentLanguage].filter(
      (_, i) => i !== index
    );

    setAttachmentsByLanguage((prev) => ({
      ...prev,
      [currentLanguage]: updatedAttachments,
    }));

    if (onDeleteAttachment) {
      onDeleteAttachment(currentLanguage, deletedId);
    }

    if (onAttachmentsChange) {
      onAttachmentsChange({
        mainImage: mainImage.length > 0 ? mainImage : null,
        report: updatedAttachments,
      });
    }
  };

  return (
    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Upload Main Image */}
      {showMainImage && (
        <ImageUploading
          value={mainImage}
          onChange={handleMainImageUpload}
          dataURLKey="data_url"
        >
          {({ onImageUpload }) => (
            <Box>
              <Button
                sx={{
                  backgroundColor: "#353634",
                  color: "#fff",
                  ":hover": { backgroundColor: "#282828" },
                  width: "100%",
                }}
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={onImageUpload}
              >
                Main Image
              </Button>
              <Box mt={2} display="flex" alignItems="center">
                {mainImage.length > 0 && !deleteMainImage ? (
                  mainImage.map((image, index) => (
                    <Box
                      key={index}
                      sx={{ position: "relative", display: "inline-block" }}
                    >
                      <img
                        src={encodeURI(image.data_url)}
                        alt={`main_image_${index}`}
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                      <IconButton
                        onClick={handleRemoveMainImage}
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          background: "rgba(255, 255, 255, 0.7)",
                          borderRadius: "50%",
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))
                ) : media?.mainImage && !deleteMainImage ? (
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    <img
                      src={encodeURI(media.mainImage)}
                      alt="main_image"
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                    <IconButton
                      onClick={handleRemoveMainImage}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        background: "rgba(255, 255, 255, 0.7)",
                        borderRadius: "50%",
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : null}
              </Box>
            </Box>
          )}
        </ImageUploading>
      )}

      {/* Divider */}
      {showMainImage && showReport && (
        <Divider sx={{ borderBottom: "1px solid #ccc", my: 2 }} />
      )}

      {/* Upload Attachments for the selected language */}
      {showReport && (
        <Box>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            multiple
            accept="image/*,.pdf"
            onChange={handleAttachmentFileChange}
          />
          <Button
            sx={{
              backgroundColor: "#353634",
              color: "#fff",
              ":hover": { backgroundColor: "#282828" },
              width: "100%",
            }}
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            Attachment ({currentLanguage})
          </Button>
          <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
            {attachmentsByLanguage[currentLanguage]?.length > 0 ? (
              attachmentsByLanguage[currentLanguage].map((file, index) => (
                <Box
                  key={index}
                  sx={{ position: "relative", display: "inline-block" }}
                >
                  {isPDF(file) ? (
                    // PDF Preview
                    <Box
                      sx={{
                        width: "100px",
                        height: "100px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                      }}
                    >
                      <PictureAsPdfIcon
                        sx={{ fontSize: 40, color: "#d32f2f" }}
                      />
                      <Box
                        sx={{ fontSize: "10px", textAlign: "center", mt: 1 }}
                      >
                        PDF
                      </Box>
                    </Box>
                  ) : (
                    <img
                      src={encodeURI(file.data_url)}
                      alt={`attachment_${index}`}
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                  )}
                  <IconButton
                    onClick={() => handleRemoveAttachment(index)}
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      background: "rgba(255, 255, 255, 0.7)",
                      borderRadius: "50%",
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            ) : (
              <p>No attachments for this language</p>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
